package com.trevorwiebe.timesheet.loads.presentation.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Share
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.loads.domain.model.Load
import com.trevorwiebe.timesheet.loads.domain.model.LoadStop
import com.trevorwiebe.timesheet.loads.domain.model.drops
import com.trevorwiebe.timesheet.loads.domain.model.pickups
import com.trevorwiebe.timesheet.mileage.domain.model.Location
import com.trevorwiebe.timesheet.mileage.domain.model.cityState

private val ConfirmGreen = Color(0xFF34C759)
private val SetUpBlue = Color(0xFF007AFF)

// Fixed so a long driver name never squeezes the pickup/drop columns out of line; the Completed
// table header uses it too.
internal val CompletedTrailingWidth = 84.dp
internal val LoadCardStartPadding = 8.dp
internal val StatusBadgeWidth = 10.dp

@Composable
internal fun LoadCard(
    load: Load,
    locations: List<Location>,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    onToggleConfirm: (() -> Unit)? = null,
    onShare: (() -> Unit)? = null,
    isDragging: Boolean = false,
    showBottomDivider: Boolean = true,
    // The dispatch board passes 0 so card content lines up with the gutter edge (and the
    // "Off" pill); list screens keep the default inset.
    startPadding: Dp = LoadCardStartPadding,
    // Completed screen variant: replaces the pickup date (and rate category) with a trailing date
    // over the driver's name.
    trailingDate: String? = null,
    driverName: String? = null,
) {
    fun entryOf(stop: LoadStop): Pair<String, String> {
        val location = locations.firstOrNull { it.id == stop.locationId }
        return (location?.name ?: "—") to location?.cityState.orEmpty()
    }
    val pickupEntries = load.pickups.map { entryOf(it) }
    val dropEntries = load.drops.map { entryOf(it) }
    val pickupDateText = Util.toNumericMonthDay(load.pickupDate)

    Column(
        // Caller modifier first so offset/size/alpha from the drag ghost and drag source wrap the
        // whole card, background included.
        modifier = modifier
            .fillMaxWidth()
            .background(
                if (isDragging) MaterialTheme.colorScheme.surface
                else MaterialTheme.colorScheme.background
            ),
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .clickable(onClick = onClick)
                // Preserves the row height the arrow slots used to establish.
                .heightIn(min = 36.dp)
                .padding(start = startPadding, end = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            StatusBadgeColumn(setUp = load.setUp, ready = load.ready)
            NameColumn(
                entries = pickupEntries,
                textAlign = TextAlign.Start,
                modifier = Modifier.weight(1f),
            )
            Spacer(Modifier.width(8.dp))
            NameColumn(
                entries = dropEntries,
                textAlign = TextAlign.Start,
                modifier = Modifier.weight(1f),
            )
            Spacer(Modifier.width(8.dp))
            if (trailingDate != null) {
                Column(
                    modifier = Modifier.width(CompletedTrailingWidth),
                    horizontalAlignment = Alignment.End,
                ) {
                    Text(
                        text = trailingDate,
                        fontSize = 14.sp,
                        lineHeight = 16.sp,
                        maxLines = 1,
                    )
                    driverName?.let {
                        Text(
                            text = it,
                            fontSize = 10.sp,
                            lineHeight = 12.sp,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                        )
                    }
                }
            } else {
                // Fixed width so a longer rate category never pushes the drop column around.
                Column(
                    modifier = Modifier.width(52.dp),
                    horizontalAlignment = Alignment.End,
                ) {
                    Text(
                        text = pickupDateText,
                        fontSize = 14.sp,
                        lineHeight = 16.sp,
                        maxLines = 1,
                    )
                    load.rate?.let {
                        Text(
                            text = it.categoryLabel,
                            fontSize = 10.sp,
                            lineHeight = 12.sp,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                        )
                    }
                }
            }
            if (onToggleConfirm != null) {
                Spacer(Modifier.width(8.dp))
                Box(
                    modifier = Modifier
                        .size(28.dp)
                        .clip(CircleShape)
                        .then(
                            if (load.dispatchConfirmed) Modifier.background(ConfirmGreen)
                            else Modifier.border(2.dp, ConfirmGreen, CircleShape)
                        )
                        .clickable(onClick = onToggleConfirm),
                    contentAlignment = Alignment.Center,
                ) {
                    Icon(
                        Icons.Default.Check,
                        contentDescription = if (load.dispatchConfirmed) "Confirmed" else "Confirm",
                        tint = if (load.dispatchConfirmed) Color.White else ConfirmGreen,
                        modifier = Modifier.size(16.dp),
                    )
                }
            }
            if (onShare != null) {
                Spacer(Modifier.width(8.dp))
                Box(
                    // Sized to stay under the row's 36.dp min height so the row doesn't grow.
                    modifier = Modifier
                        .size(34.dp)
                        .clip(CircleShape)
                        .clickable(onClick = onShare),
                    contentAlignment = Alignment.Center,
                ) {
                    Icon(
                        Icons.Default.Share,
                        contentDescription = "Share load confirmation",
                        tint = MaterialTheme.colorScheme.tertiary,
                        modifier = Modifier.size(22.dp),
                    )
                }
            }
        }
        if (showBottomDivider && !isDragging) HorizontalDivider()
    }
}

@Composable
private fun StatusBadgeColumn(setUp: Boolean, ready: Boolean) {
    Column(
        modifier = Modifier.width(StatusBadgeWidth),
        verticalArrangement = Arrangement.spacedBy(4.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        StatusBadge(visible = setUp, color = SetUpBlue)
        StatusBadge(visible = ready, color = ConfirmGreen)
    }
}

@Composable
private fun StatusBadge(visible: Boolean, color: Color) {
    Box(
        Modifier
            .size(6.dp)
            .clip(CircleShape)
            .background(if (visible) color else Color.Transparent)
    )
}

/** Each entry is a location name over its "City, ST" subtitle. Line heights are kept tight so a
 *  single entry still fits inside the 36.dp arrow-slot height and the card doesn't grow. */
@Composable
private fun NameColumn(
    entries: List<Pair<String, String>>,
    textAlign: TextAlign,
    modifier: Modifier = Modifier,
) {
    Column(modifier = modifier) {
        if (entries.isEmpty()) {
            Text(text = "", fontSize = 14.sp, maxLines = 1)
        } else {
            entries.forEach { (name, cityState) ->
                Text(
                    text = name,
                    modifier = Modifier.fillMaxWidth(),
                    fontSize = 14.sp,
                    lineHeight = 16.sp,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    textAlign = textAlign,
                )
                if (cityState.isNotEmpty()) {
                    Text(
                        text = cityState,
                        modifier = Modifier.fillMaxWidth(),
                        fontSize = 10.sp,
                        lineHeight = 12.sp,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        textAlign = textAlign,
                    )
                }
            }
        }
    }
}

